import React, { useLayoutEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import NavPillTabs from './NavPillTabs';
import imtLogo from '../../assets/images/IMT_logo.png';
import './HMSPageHeader.css';

/**
 * HMSPageHeader — the one screen header (flat deep-green band, hairline edge,
 * brand | kicker + title lockup, centred pill tray, trailing actions).
 *
 * Layout contract, owned here so screens never write header CSS again:
 * the brand block and the trailing actions cluster sit in equal flex
 * zones with the pill tray natural-width between them, which puts the
 * tray on the page's centre line and lets both zones absorb pill hover
 * growth symmetrically. Because the zones are equal, the row holds only
 * while TWICE the wider cluster plus the tray fits — measured live,
 * including the width the widest pill would add on hover. Past that the
 * tray moves to its own full-width centred row (`data-stacked`).
 *
 *   title      string, required — calm bold title, no uppercase forcing
 *   kicker     optional module label above the title (e.g. "Finance")
 *   subtitle   optional muted line under the title
 *   tabs       optional { items, activeKey, onChange, alwaysLabel, dataTour } → NavPillTabs;
 *              dataTour lands on the whole pill tray, so a walkthrough step can
 *              point at the tab bar (each pill keeps its own item.dataTour)
 *   actions    optional right-cluster content (buttons, chips, pickers)
 *   onBack     optional handler → back button, left edge
 *   backLabel  optional words for the back button (hover text and screen-reader
 *              name), e.g. "Back to Invoicing Dashboard"; defaults to "Go back"
 *   className  optional screen hook
 *   dataTour   optional walkthrough anchor for the whole header (data-tour)
 */
const HMSPageHeader = ({ title, kicker, subtitle, tabs, actions, onBack, backLabel, className = '', dataTour }) => {
  const headerRef = useRef(null);
  const brandRef = useRef(null);
  const trayRef = useRef(null);
  const actionsRef = useRef(null);
  const [stacked, setStacked] = useState(false);

  useLayoutEffect(() => {
    if (!tabs) {
      setStacked(false);
      return undefined;
    }
    const header = headerRef.current;
    const trayInner = trayRef.current
      ? trayRef.current.querySelector('.nav-pill-tray')
      : null;
    if (!header || !trayInner || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const measure = () => {
      const brand = brandRef.current;
      const acts = actionsRef.current;
      if (!brand) return;
      // A hovered pill has the tray mid-growth; measuring now would flap
      // the stacked state. The rest state re-measures on mouseleave.
      if (trayInner.matches(':hover')) return;
      const cs = getComputedStyle(header);
      const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const gap = parseFloat(cs.columnGap) || 16;
      // Hover headroom: the widest collapsed label bounds how much a
      // single hover can grow the tray (label + column-gap + pad delta).
      // A labelled tray has its labels out already, so hover grows it by
      // nothing and holding headroom would stack the row for no reason.
      let headroom = 0;
      if (!trayInner.classList.contains('nav-pill-tray--labelled')) {
        trayInner
          .querySelectorAll('.nav-pill:not(.active) .nav-pill-label')
          .forEach((el) => {
            headroom = Math.max(headroom, el.scrollWidth + 14);
          });
      }
      // When stacked the tray spans its row, so offsetWidth is inflated;
      // scrollWidth still reports the plate's natural width.
      const trayWidth = Math.max(trayInner.offsetWidth, trayInner.scrollWidth);
      // The zones are equal-width (that is what centres the tray), so the
      // row needs TWICE the wider cluster, not the sum of both: a wide
      // actions cluster overflows its half-zone and slides under the tray
      // long before brand + tray + actions outgrow the row.
      const zone = Math.max(brand.offsetWidth, acts ? acts.offsetWidth : 0);
      const need = zone * 2 + trayWidth + pad + gap * 2 + headroom;
      setStacked(need > header.clientWidth);
    };

    const ro = new ResizeObserver(measure);
    ro.observe(header);
    ro.observe(trayInner);
    if (brandRef.current) ro.observe(brandRef.current);
    if (actionsRef.current) ro.observe(actionsRef.current);
    measure();
    return () => ro.disconnect();
  }, [tabs]);

  return (
    <header
      ref={headerRef}
      className={`hms-page-header ${onBack ? 'hms-page-header--back' : ''} ${className}`}
      data-stacked={stacked || undefined}
      data-tour={dataTour || undefined}
    >
      {onBack && (
        <button
          type="button"
          className="hms-page-header-back"
          onClick={onBack}
          aria-label={backLabel || 'Go back'}
          title={backLabel || undefined}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
      )}
      <div className="hms-page-header-zone hms-page-header-zone-start">
        <div className="hms-page-header-brand" ref={brandRef}>
          <img
            className="hms-page-header-logo"
            src={imtLogo}
            alt="IMT Bergbau AG"
          />
          <span className="hms-page-header-rule" aria-hidden="true" />
          <div className="hms-page-header-titles">
            {kicker && <p className="hms-page-header-kicker">{kicker}</p>}
            <h1 className="hms-page-header-title">{title}</h1>
            {subtitle && <p className="hms-page-header-subtitle">{subtitle}</p>}
          </div>
        </div>
      </div>
      {tabs && (
        <div className="hms-page-header-tray" ref={trayRef} data-tour={tabs.dataTour || undefined}>
          <NavPillTabs
            items={tabs.items}
            activeKey={tabs.activeKey}
            onChange={tabs.onChange}
            alwaysLabel={tabs.alwaysLabel}
          />
        </div>
      )}
      <div className="hms-page-header-zone hms-page-header-zone-end">
        <div className="hms-page-header-actions" ref={actionsRef}>
          {actions}
        </div>
      </div>
    </header>
  );
};

export default HMSPageHeader;
